import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { leadMagnet } from "@/lib/api";
import { Loader2, Download, FileText } from "lucide-react";

export const LeadMagnetDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  const reset = () => { setEmail(""); setLoading(false); setDone(false); setPdfUrl(""); };

  const triggerDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "AskTasha-Launch-Blueprint.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await leadMagnet(email.trim());
      setPdfUrl(res.pdf_url);
      setDone(true);
      toast.success("Your Blueprint is downloading!");
      setTimeout(() => triggerDownload(res.pdf_url), 500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent data-testid="lead-magnet-dialog" className="border-gold/25 bg-ink-elevated text-cream sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-semibold tracking-tight text-cream">
            {done ? "Enjoy the Blueprint." : "Get the Free Blueprint"}
          </DialogTitle>
          <p className="pt-1 font-sans text-sm text-cream/60">
            The Home Care Agency Launch Blueprint · PDF
          </p>
          <DialogDescription className="sr-only">
            Enter your email to instantly download the free Home Care Agency Launch Blueprint PDF.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="lead-magnet-success">
            <FileText className="text-gold" size={54} />
            <p className="font-sans text-sm text-cream/70">
              Your download should start automatically. If it didn't, grab it below.
            </p>
            <a
              href={pdfUrl}
              download="AskTasha-Launch-Blueprint.pdf"
              data-testid="lead-magnet-download-link"
              className="btn-gold w-full"
            >
              Download the Blueprint <Download size={16} />
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 pt-2" data-testid="lead-magnet-form">
            <div className="space-y-2">
              <Label htmlFor="lm-email" className="overline">Email Address</Label>
              <Input
                id="lm-email"
                data-testid="lead-magnet-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="border-cream/15 bg-ink text-cream placeholder:text-cream/30 focus-visible:ring-gold"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="lead-magnet-submit-btn" className="btn-gold w-full disabled:opacity-60">
              {loading ? (<><Loader2 className="animate-spin" size={16} /> Preparing…</>) : (<>Send Me the Blueprint <Download size={16} /></>)}
            </button>
            <p className="text-center font-sans text-xs text-cream/40">
              Instant download. We'll also keep you posted on upcoming classes.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadMagnetDialog;
